/**
 * Table — the wood-and-felt world the cards live on.
 *
 * Structure (back to front):
 *   1. The room: a warm radial wash (sunroom daylight in light mode, a lamp
 *      pool in dark mode) painted in CSS.
 *   2. The wood surround: a layered gradient with an feTurbulence grain filter
 *      (structural SVG noise — allowed) so it reads as real timber, not a flat
 *      brown box.
 *   3. The felt playing field: a recessed wool surface with a soft inner
 *      shadow and its own finer grain, lit from the same direction as the room.
 *
 * If/when the asset pipeline produces `solitaire-table-*` imagery
 * (manifest `solitaire-table-v1.yaml`), drop an <AtmosphereSurface> into
 * `.sol-table__wood` / `.sol-table__felt` — the CSS here is the considered
 * fallback and the integration target, not a throwaway.
 */
import type { ReactNode } from 'react';

import './Solitaire.css';

export function Table({ children }: { children: ReactNode }): React.JSX.Element {
  return (
    <div className="sol-table">
      <div className="sol-table__room" aria-hidden="true" />
      <div className="sol-table__wood" aria-hidden="true">
        <GrainFilter id="sol-wood-grain" baseFrequency="0.012 0.16" octaves={3} seed={7} alpha={0.16} />
      </div>
      <div className="sol-table__felt" aria-hidden="true">
        <GrainFilter id="sol-felt-grain" baseFrequency="0.9" octaves={2} seed={4} alpha={0.05} />
        <div className="sol-table__felt-light" aria-hidden="true" />
      </div>
      <div className="sol-table__content">{children}</div>
    </div>
  );
}

/** A reusable feTurbulence grain overlay (structural noise, not depiction). */
function GrainFilter({
  id,
  baseFrequency,
  octaves,
  seed,
  alpha,
}: {
  id: string;
  baseFrequency: string;
  octaves: number;
  seed: number;
  alpha: number;
}): React.JSX.Element {
  return (
    <svg className="sol-grain" width="100%" height="100%" aria-hidden="true" preserveAspectRatio="none">
      <defs>
        <filter id={id} x="0" y="0" width="100%" height="100%">
          <feTurbulence type="fractalNoise" baseFrequency={baseFrequency} numOctaves={octaves} seed={seed} stitchTiles="stitch" />
          <feColorMatrix values={`0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 ${alpha} 0`} />
        </filter>
      </defs>
      <rect width="100%" height="100%" filter={`url(#${id})`} />
    </svg>
  );
}
